import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import {
  Loader2, Plus, Pencil, Trash2, ChevronDown, ChevronRight, X, Tag, LayoutGrid, Banknote, Layers, Clock, Users,
} from 'lucide-react';
import {
  AdminPage, EmptyState, FilterChip, PageHeader, Panel, StatCard, StatusChip, Toolbar, adminButton, personHue,
  useAdminPageTitle, type AdminTone,
} from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import { formatMoney } from '@/lib/currency';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { setServiceChecklistTemplate } from '@/hooks/useChecklists';
import { ServiceChecklistField } from '@/components/admin/checklists/ServiceChecklistField';

interface ServiceVariant {
  id: string;
  service_id: string;
  name: string;
  duration: string;
  price: string | number | null;
  sort_order: number;
}

interface EditableServiceVariant extends Omit<ServiceVariant, 'price'> {
  price: string;
}

interface Service {
  id: string;
  name: string;
  category: string | null;
  duration: string;
  resource_required: number;
  price: string | number | null;
  is_active: boolean;
  is_quote_only: boolean;
  sort_order: number;
  variants?: ServiceVariant[];
}

interface NewVariant {
  name: string;
  duration: string;
  price: string;
}

const DURATION_OPTIONS = [
  '30 mins',
  '1 hour',
  '1.5 hours',
  '2 hours',
  '2.5 hours',
  '3 hours',
  '3.5 hours',
  '4 hours',
  '4.5 hours',
  '5 hours',
  '5.5 hours',
  '6 hours',
  '6.5 hours',
  '7 hours',
  '7.5 hours',
  '8 hours',
];

const RESOURCE_OPTIONS = [1, 2, 3, 4, 5];

const CATEGORY_OPTIONS = [
  'Regular Domestic Cleaning',
  'Airbnb Turnover Cleaning',
  'End of Tenancy Cleaning',
  'Deep Cleaning',
  'After Builders Cleaning',
  'Carpet Cleaning',
  'Upholstery Cleaning',
  'Commercial Property',
];

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error && 'message' in error) return String((error as { message?: unknown }).message);
  if (typeof error === 'object' && error && 'code' in error) return String((error as { code?: unknown }).code);
  return String(error);
};

const toNumericPrice = (price: string) => Number.parseFloat(price.trim());

const UNCATEGORISED = '__uncategorised__';
const CATEGORY_TONES: AdminTone[] = ['violet', 'mint', 'blue', 'amber', 'rose', 'green'];

const toneForCategory = (category: string): AdminTone => {
  let hash = 0;
  for (let i = 0; i < category.length; i++) hash = (hash * 31 + category.charCodeAt(i)) >>> 0;
  return CATEGORY_TONES[hash % CATEGORY_TONES.length];
};

/** Category pill — stable tone per category name. */
const CategoryChip = ({ category }: { category: string }) => (
  <StatusChip tone={toneForCategory(category)} dot={false} className="max-w-full truncate">
    {category}
  </StatusChip>
);

/** Rounded-square initial tile for a service (decorative). */
const ServiceMark = ({ name }: { name: string }) => {
  const hue = personHue(name);
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      aria-hidden="true"
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-[12.5px] font-semibold"
      style={{ background: `hsl(${hue} 70% 94%)`, color: `hsl(${hue} 45% 34%)`, boxShadow: `inset 0 0 0 1px hsl(${hue} 50% 86%)` }}
    >
      {initial}
    </span>
  );
};

const JobsPricingAdmin = () => {
  useAdminPageTitle('Jobs and Pricing');
  const { orgId, loading: orgLoading, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteVariantDialogOpen, setDeleteVariantDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);
  const [variantToDelete, setVariantToDelete] = useState<ServiceVariant | null>(null);
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    duration: '1 hour',
    resource_required: 1,
    price: '',
    is_quote_only: false,
  });
  const [variants, setVariants] = useState<NewVariant[]>([]);
  // Default checklist for the service; undefined means "not changed in this dialog".
  const [checklistTemplateId, setChecklistTemplateId] = useState<string | null | undefined>(undefined);
  const [existingVariants, setExistingVariants] = useState<EditableServiceVariant[]>([]);
  const { toast } = useToast();
  const { data: orgSettings } = useOrgSettings(orgId);
  const currency = orgSettings?.currency;

  const fetchServices = useCallback(async () => {
    if (!orgId) return;
    
    try {
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('id, name, category, duration, resource_required, price, is_active, is_quote_only, sort_order')
        .eq('organization_id', orgId)
        .order('sort_order', { ascending: true });

      if (servicesError) throw servicesError;

      const { data: variantsData, error: variantsError } = await supabase
        .from('service_variants')
        .select('*')
        .eq('organization_id', orgId)
        .order('sort_order', { ascending: true });

      if (variantsError) throw variantsError;

      const servicesWithVariants = (servicesData || []).map(service => ({
        ...service,
        variants: (variantsData || []).filter(v => v.service_id === service.id),
      }));

      setServices(servicesWithVariants);
    } catch (error: unknown) {
      console.error('Error fetching services:', error);
      toast({
        title: 'Error',
        description: `Failed to load services: ${getErrorMessage(error)}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => {
    if (orgLoading) return;

    if (!orgId) {
      setLoading(false);
      return;
    }

    fetchServices();
  }, [fetchServices, orgId, orgLoading]);

  const toggleServiceExpanded = (serviceId: string) => {
    setExpandedServices(prev => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  const openAddDialog = () => {
    if (!assertCanWrite()) return;
    setEditingService(null);
    setFormData({
      name: '',
      category: '',
      duration: '1 hour',
      resource_required: 1,
      price: '',
      is_quote_only: false,
    });
    setVariants([]);
    setExistingVariants([]);
    setChecklistTemplateId(undefined);
    setDialogOpen(true);
  };

  const openEditDialog = (service: Service) => {
    if (!assertCanWrite()) return;
    setEditingService(service);
    setFormData({
      name: service.name,
      category: service.category || '',
      duration: service.duration,
      resource_required: service.resource_required,
      // price is numeric in the DB, so coerce to the string the editable form expects.
      price: service.price == null ? '' : String(service.price),
      is_quote_only: service.is_quote_only || false,
    });
    setVariants([]);
    setExistingVariants(
      (service.variants || []).map((v) => ({ ...v, price: v.price == null ? '' : String(v.price) })),
    );
    setChecklistTemplateId(undefined);
    setDialogOpen(true);
  };

  const openDeleteDialog = (service: Service) => {
    if (!assertCanWrite()) return;
    setServiceToDelete(service);
    setDeleteDialogOpen(true);
  };

  const openDeleteVariantDialog = (variant: ServiceVariant) => {
    if (!assertCanWrite()) return;
    setVariantToDelete(variant);
    setDeleteVariantDialogOpen(true);
  };

  const addVariantRow = () => {
    if (!assertCanWrite()) return;
    setVariants([...variants, { name: '', duration: '1 hour', price: '' }]);
  };

  const updateVariant = (index: number, field: keyof NewVariant, value: string) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    setVariants(updated);
  };

  const removeVariantRow = (index: number) => {
    if (!assertCanWrite()) return;
    setVariants(variants.filter((_, i) => i !== index));
  };

  const updateExistingVariant = (id: string, field: keyof NewVariant, value: string) => {
    setExistingVariants(prev => 
      prev.map(v => v.id === id ? { ...v, [field]: value } : v)
    );
  };

  const handleSave = async () => {
    if (!assertCanWrite()) return;
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in service name',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.is_quote_only && !formData.price.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in price or mark as quote-only',
        variant: 'destructive',
      });
      return;
    }

    // Validate new variants
    for (const variant of variants) {
      if (variant.name.trim() && !variant.price.trim()) {
        toast({
          title: 'Validation Error',
          description: 'All variants must have a price',
          variant: 'destructive',
        });
        return;
      }
    }

    setSaving(true);
    try {
      let serviceId = editingService?.id;

      if (editingService) {
        const { error } = await supabase
          .from('services')
          .update({
            name: formData.name.trim(),
            category: formData.category || null,
            duration: formData.duration,
            resource_required: formData.resource_required,
            price: formData.is_quote_only ? 0 : toNumericPrice(formData.price),
            is_quote_only: formData.is_quote_only,
          })
          .eq('id', editingService.id);

        if (error) throw error;

        // Update existing variants
        for (const variant of existingVariants) {
          const { error: variantError } = await supabase
            .from('service_variants')
            .update({
              name: variant.name.trim(),
              duration: variant.duration,
              price: toNumericPrice(variant.price),
            })
            .eq('id', variant.id);

          if (variantError) throw variantError;
        }
      } else {
        const maxSortOrder = services.length > 0 
          ? Math.max(...services.map(s => s.sort_order || 0)) 
          : 0;

        const { data, error } = await supabase.from('services').insert({
          name: formData.name.trim(),
          category: formData.category || null,
          duration: formData.duration,
          resource_required: formData.resource_required,
          price: formData.is_quote_only ? 0 : toNumericPrice(formData.price),
          is_quote_only: formData.is_quote_only,
          sort_order: maxSortOrder + 1,
          is_active: true,
          organization_id: orgId,
        }).select('id').single();

        if (error) throw error;
        serviceId = data.id;
      }

      // Add new variants
      const validVariants = variants.filter(v => v.name.trim() && v.price.trim());
      if (validVariants.length > 0 && serviceId) {
        const variantsToInsert = validVariants.map((v, index) => ({
          service_id: serviceId,
          name: v.name.trim(),
          duration: v.duration,
          price: toNumericPrice(v.price),
          sort_order: (existingVariants.length || 0) + index + 1,
          organization_id: orgId,
        }));

        const { error: variantsError } = await supabase
          .from('service_variants')
          .insert(variantsToInsert);

        if (variantsError) throw variantsError;
      }

      if (serviceId && checklistTemplateId !== undefined) {
        await setServiceChecklistTemplate(serviceId, checklistTemplateId);
      }

      toast({ title: 'Success', description: editingService ? 'Service updated successfully' : 'Service added successfully' });
      setDialogOpen(false);
      
      // Auto-expand if we added variants
      if (serviceId && (validVariants.length > 0 || existingVariants.length > 0)) {
        setExpandedServices(prev => new Set(prev).add(serviceId));
      }
      
      fetchServices();
    } catch (error) {
      console.error('Error saving service:', error);
      toast({
        title: 'Error',
        description: 'Failed to save service',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!assertCanWrite()) return;
    if (!serviceToDelete) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', serviceToDelete.id);

      if (error) throw error;
      toast({ title: 'Success', description: 'Service deleted successfully' });
      setDeleteDialogOpen(false);
      setServiceToDelete(null);
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete service',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVariant = async () => {
    if (!assertCanWrite()) return;
    if (!variantToDelete) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('service_variants')
        .delete()
        .eq('id', variantToDelete.id);

      if (error) throw error;
      toast({ title: 'Success', description: 'Variant deleted successfully' });
      setDeleteVariantDialogOpen(false);
      setVariantToDelete(null);
      
      // Also remove from existingVariants if dialog is open
      setExistingVariants(prev => prev.filter(v => v.id !== variantToDelete.id));
      
      fetchServices();
    } catch (error) {
      console.error('Error deleting variant:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete variant',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const filteredServices = categoryFilter === null
    ? services
    : services.filter((s) => (s.category || UNCATEGORISED) === categoryFilter);
  const pricedServices = services.filter((s) => !s.is_quote_only && s.price != null && Number.isFinite(Number(s.price)));
  const averagePrice = pricedServices.length > 0
    ? pricedServices.reduce((total, s) => total + Number(s.price), 0) / pricedServices.length
    : null;
  const pricedValues = pricedServices.map((s) => Number(s.price));
  const minPrice = pricedValues.length > 0 ? Math.min(...pricedValues) : null;
  const maxPrice = pricedValues.length > 0 ? Math.max(...pricedValues) : null;
  const variantCount = services.reduce((total, s) => total + (s.variants?.length || 0), 0);
  const quoteOnlyCount = services.filter((s) => s.is_quote_only).length;
  const categoryCounts = services.reduce<Map<string, number>>((map, s) => {
    const key = s.category || UNCATEGORISED;
    map.set(key, (map.get(key) || 0) + 1);
    return map;
  }, new Map());
  const categoryKeys = Array.from(categoryCounts.keys());
  const emptyCopy = 'No services added yet. Click "Add Service" to get started.';
  const peopleLabel = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AdminPage>
      <PageHeader
        description="Manage services and their variants"
        actions={
          <Button onClick={openAddDialog} disabled={!canWrite} size="sm" className={cn(adminButton.primary, 'w-full sm:w-auto')}>
            <Plus />
            Add Service
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-4">
        <StatCard label="Services" icon={<Tag />} value={services.length} hint={`${services.length - quoteOnlyCount} with a fixed price`} />
        <StatCard label="Categories" icon={<LayoutGrid />} value={categoryKeys.filter((c) => c !== UNCATEGORISED).length} hint="Group your booking page" />
        <StatCard
          label="Average price"
          icon={<Banknote />}
          value={averagePrice == null ? '—' : formatMoney(averagePrice, currency)}
          hint={`Across ${pricedServices.length} priced ${pricedServices.length === 1 ? 'service' : 'services'}`}
        />
        <StatCard
          label="Price range"
          icon={<Layers />}
          value={minPrice == null || maxPrice == null ? '—' : minPrice === maxPrice ? formatMoney(minPrice, currency) : `${formatMoney(minPrice, currency)}–${formatMoney(maxPrice, currency)}`}
          hint={variantCount > 0 ? `${variantCount} pricing ${variantCount === 1 ? 'variant' : 'variants'}` : quoteOnlyCount > 0 ? `${quoteOnlyCount} quote-only` : 'Lowest to highest'}
        />
      </div>

      {categoryKeys.length > 1 && (
        <Toolbar aria-label="Filter services by category">
          <FilterChip active={categoryFilter === null} count={services.length} onClick={() => setCategoryFilter(null)}>
            All services
          </FilterChip>
          {categoryKeys.map((cat) => (
            <FilterChip
              key={cat}
              active={categoryFilter === cat}
              count={categoryCounts.get(cat)}
              onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
            >
              {cat === UNCATEGORISED ? 'Uncategorised' : cat}
            </FilterChip>
          ))}
        </Toolbar>
      )}

      {/* Mobile list view */}
      <Panel
        className="block md:hidden"
        title="Services"
        description={`${services.length} services configured`}
        padded={false}
      >
        {services.length === 0 ? (
          <EmptyState icon={<Tag />} title="No services yet" description={emptyCopy} />
        ) : (
          <div className="adm-divide mt-3 border-t border-border">
            {filteredServices.map((service) => {
              const hasVariants = (service.variants?.length || 0) > 0;
              const isExpanded = expandedServices.has(service.id);

              return (
                <div key={service.id} className="px-4 py-3.5">
                  <div className="flex items-start gap-3">
                    <ServiceMark name={service.name} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-[13.5px] font-semibold text-foreground">{service.name}</p>
                          <p className="mt-0.5 text-[12px] text-muted-foreground">
                            {[service.duration, peopleLabel(service.resource_required)].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                        <p className="adm-num shrink-0 text-[14px] font-semibold text-foreground">
                          {service.is_quote_only ? <span className="text-[12.5px] font-medium italic text-muted-foreground">Quote</span> : formatMoney(service.price, currency)}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          {service.category ? <CategoryChip category={service.category} /> : <span className="text-[12px] text-muted-foreground">No category</span>}
                        </div>
                        <div className="flex shrink-0 items-center gap-0.5">
                          <Button disabled={!canWrite} variant="ghost" size="sm" className={adminButton.iconGhost} aria-label="Edit service" onClick={() => openEditDialog(service)}>
                            <Pencil />
                          </Button>
                          <Button disabled={!canWrite} variant="ghost" size="sm" className={cn(adminButton.iconGhost, 'hover:text-destructive')} aria-label="Delete service" onClick={() => openDeleteDialog(service)}>
                            <Trash2 />
                          </Button>
                        </div>
                      </div>
                      {hasVariants && (
                        <div className="mt-2.5">
                          <button
                            type="button"
                            onClick={() => toggleServiceExpanded(service.id)}
                            className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[12.5px] font-medium text-primary hover:bg-secondary"
                          >
                            {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            {service.variants?.length} variant{service.variants?.length !== 1 ? 's' : ''}
                          </button>
                          {isExpanded && (
                            <div className="mt-2 space-y-1.5">
                              {service.variants?.map((variant) => (
                                <div key={variant.id} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 py-1.5 pl-3 pr-1">
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-[12.5px] font-medium text-foreground">{variant.name}</p>
                                    <p className="text-[11.5px] text-muted-foreground">{variant.duration} · <span className="adm-num">{formatMoney(variant.price, currency)}</span></p>
                                  </div>
                                  <Button disabled={!canWrite} variant="ghost" size="sm" className={cn(adminButton.iconGhost, 'h-7 w-7 hover:text-destructive')} aria-label="Delete variant" onClick={() => openDeleteVariantDialog(variant)}>
                                    <Trash2 />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* Desktop table view */}
      <Panel
        className="hidden overflow-hidden md:block"
        title="Services"
        description={`${services.length} services configured`}
        padded={false}
        bodyClassName="mt-4"
      >
        <Table className="adm-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[44px] !pr-0"><span className="sr-only">Variants</span></TableHead>
              <TableHead>Service Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Resources</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="w-[96px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {services.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState icon={<Tag />} title="No services yet" description={emptyCopy} />
                </TableCell>
              </TableRow>
            ) : (
              filteredServices.map((service) => {
                const hasVariants = (service.variants?.length || 0) > 0;
                const isExpanded = expandedServices.has(service.id);

                return (
                  <Collapsible key={service.id} open={isExpanded} asChild>
                    <>
                      <TableRow className="group">
                        <TableCell className="!pr-0">
                          {hasVariants && (
                            <CollapsibleTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={cn(adminButton.iconGhost, 'h-7 w-7')}
                                aria-label={isExpanded ? 'Hide variants' : 'Show variants'}
                                onClick={() => toggleServiceExpanded(service.id)}
                              >
                                {isExpanded ? <ChevronDown /> : <ChevronRight />}
                              </Button>
                            </CollapsibleTrigger>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <ServiceMark name={service.name} />
                            <div className="min-w-0">
                              <p className="truncate text-[13.5px] font-semibold text-foreground">{service.name}</p>
                              {hasVariants && (
                                <p className="text-[11.5px] text-muted-foreground">
                                  {service.variants?.length} variant{service.variants?.length !== 1 ? 's' : ''}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {service.category ? <CategoryChip category={service.category} /> : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {service.duration ? (
                            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{service.duration}</span>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{peopleLabel(service.resource_required)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {service.is_quote_only
                            ? <span className="italic text-muted-foreground">Get a Quote</span>
                            : <span className="adm-num text-[13.5px] font-semibold text-foreground">{formatMoney(service.price, currency)}</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-0.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              className={adminButton.iconGhost}
                              onClick={() => openEditDialog(service)}
                              disabled={!canWrite}
                              title="Edit service"
                            >
                              <Pencil />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(adminButton.iconGhost, 'hover:bg-destructive/10 hover:text-destructive')}
                              onClick={() => openDeleteDialog(service)}
                              disabled={!canWrite}
                              title="Delete service"
                            >
                              <Trash2 />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      <CollapsibleContent asChild>
                        <>
                          {service.variants?.map((variant) => (
                            <TableRow key={variant.id} className="bg-muted/25">
                              <TableCell></TableCell>
                              <TableCell>
                                <div className="flex items-center gap-3 pl-[11px]">
                                  <span className="h-5 w-[18px] shrink-0 rounded-bl-md border-b border-l border-border" aria-hidden="true" />
                                  <span className="text-[13px] text-foreground/85">{variant.name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground">—</TableCell>
                              <TableCell className="text-muted-foreground">{variant.duration}</TableCell>
                              <TableCell className="text-muted-foreground">—</TableCell>
                              <TableCell className="text-right">
                                <span className="adm-num text-foreground/85">{formatMoney(variant.price, currency)}</span>
                              </TableCell>
                              <TableCell>
                                <div className="flex justify-end">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={cn(adminButton.iconGhost, 'hover:bg-destructive/10 hover:text-destructive')}
                                    onClick={() => openDeleteVariantDialog(variant)}
                                    disabled={!canWrite}
                                    title="Delete variant"
                                  >
                                    <Trash2 />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                );
              })
            )}
          </TableBody>
        </Table>
      </Panel>

      {/* Add/Edit Service Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="adm-form max-h-[90vh] max-w-2xl gap-0 overflow-y-auto rounded-2xl bg-card p-0">
          <DialogHeader className="border-b border-border px-6 py-4 text-left">
            <DialogTitle className="text-[16px] font-semibold">
              {editingService ? 'Edit Service' : 'Add New Service'}
            </DialogTitle>
            <p className="text-[12.5px] text-muted-foreground">
              Set the default duration, team size and price customers see when booking.
            </p>
          </DialogHeader>
          <fieldset disabled={!canWrite} className="space-y-6 px-6 py-5 disabled:opacity-70">
            {/* Service Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="name">Service Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Regular Cleaning"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration">Default Duration *</Label>
                <Select
                  value={formData.duration}
                  onValueChange={(value) => setFormData({ ...formData, duration: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map((duration) => (
                      <SelectItem key={duration} value={duration}>
                        {duration}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="resource_required">Resources Required *</Label>
                <Select
                  value={formData.resource_required.toString()}
                  onValueChange={(value) => setFormData({ ...formData, resource_required: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RESOURCE_OPTIONS.map((num) => (
                      <SelectItem key={num} value={num.toString()}>
                        {num} {num === 1 ? 'person' : 'people'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <ServiceChecklistField
                orgId={orgId}
                serviceId={editingService?.id ?? null}
                value={checklistTemplateId}
                onChange={setChecklistTemplateId}
              />
              <div className="col-span-2 flex items-start gap-3 rounded-xl border border-border bg-muted/40 px-3.5 py-3">
                <Checkbox
                  className="mt-0.5"
                  id="is_quote_only"
                  checked={formData.is_quote_only}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_quote_only: checked === true })}
                />
                <Label htmlFor="is_quote_only" className="cursor-pointer !font-medium leading-snug">
                  Quote only (no fixed price - customers will submit an enquiry)
                </Label>
              </div>
              {!formData.is_quote_only && (
                <div className="space-y-2 col-span-2 sm:col-span-1">
                  <Label htmlFor="price">Default Price ({currency?.symbol ?? 'selected currency'}) *</Label>
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  />
                </div>
              )}
            </div>

            {/* Service Variants */}
            <div className="space-y-3 border-t border-border pt-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[14px] font-semibold text-foreground">Service Variants</p>
                  <p className="text-[12.5px] text-muted-foreground">
                    Add pricing options like 1 hour, 2 hours, etc.
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" className={adminButton.outline} disabled={!canWrite} onClick={addVariantRow}>
                  <Plus />
                  Add Variant
                </Button>
              </div>

              {/* Existing variants (when editing) */}
              {existingVariants.map((variant) => (
                <div key={variant.id} className="grid grid-cols-[1fr_auto_auto_auto] items-end gap-2 rounded-xl border border-border bg-muted/30 p-2.5">
                  <div className="space-y-1">
                    <Label className="!text-[11.5px] !font-medium text-muted-foreground">Name</Label>
                    <Input
                      value={variant.name}
                      onChange={(e) => updateExistingVariant(variant.id, 'name', e.target.value)}
                      placeholder="2 Hour Clean"
                    />
                  </div>
                  <div className="space-y-1 w-32">
                    <Label className="!text-[11.5px] !font-medium text-muted-foreground">Duration</Label>
                    <Select
                      value={variant.duration}
                      onValueChange={(value) => updateExistingVariant(variant.id, 'duration', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATION_OPTIONS.map((duration) => (
                          <SelectItem key={duration} value={duration}>
                            {duration}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 w-24">
                    <Label className="!text-[11.5px] !font-medium text-muted-foreground">Price ({currency?.symbol ?? 'selected currency'})</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={variant.price}
                      onChange={(e) => updateExistingVariant(variant.id, 'price', e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => openDeleteVariantDialog(variant)}
                    className={cn(adminButton.iconGhost, 'mb-0.5 h-9 w-9 hover:bg-destructive/10 hover:text-destructive')}
                    aria-label="Delete variant"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              {/* New variants */}
              {variants.map((variant, index) => (
                <div key={index} className="grid grid-cols-[1fr_auto_auto_auto] items-end gap-2 rounded-xl border border-border bg-muted/30 p-2.5">
                  <div className="space-y-1">
                    <Label className="!text-[11.5px] !font-medium text-muted-foreground">Name</Label>
                    <Input
                      value={variant.name}
                      onChange={(e) => updateVariant(index, 'name', e.target.value)}
                      placeholder="2 Hour Clean"
                    />
                  </div>
                  <div className="space-y-1 w-32">
                    <Label className="!text-[11.5px] !font-medium text-muted-foreground">Duration</Label>
                    <Select
                      value={variant.duration}
                      onValueChange={(value) => updateVariant(index, 'duration', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATION_OPTIONS.map((duration) => (
                          <SelectItem key={duration} value={duration}>
                            {duration}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 w-24">
                    <Label className="!text-[11.5px] !font-medium text-muted-foreground">Price ({currency?.symbol ?? 'selected currency'})</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={variant.price}
                      onChange={(e) => updateVariant(index, 'price', e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeVariantRow(index)}
                    className={cn(adminButton.iconGhost, 'mb-0.5 h-9 w-9 hover:bg-destructive/10 hover:text-destructive')}
                    aria-label="Remove variant"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              {existingVariants.length === 0 && variants.length === 0 && (
                <p className="rounded-xl border border-dashed border-border py-5 text-center text-[12.5px] text-muted-foreground">
                  No variants added. Click "Add Variant" to create pricing options.
                </p>
              )}
            </div>
          </fieldset>
          <DialogFooter className="gap-2 border-t border-border bg-muted/30 px-6 py-3.5 sm:space-x-0">
            <Button variant="outline" size="sm" className={adminButton.outline} onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" className={adminButton.primary} onClick={handleSave} disabled={!canWrite || saving}>
              {saving && <Loader2 className="animate-spin" />}
              {editingService ? 'Update' : 'Add'} Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Service Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md rounded-2xl bg-card">
          <DialogHeader className="text-left">
            <DialogTitle className="text-[16px] font-semibold">Delete Service</DialogTitle>
          </DialogHeader>
          <p className="text-[13.5px] text-muted-foreground">
            Are you sure you want to delete "{serviceToDelete?.name}"? This will also delete all its variants. This action cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:space-x-0">
            <Button variant="outline" size="sm" className={adminButton.outline} onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" className={cn(adminButton.primary, 'bg-destructive hover:bg-destructive/90')} onClick={handleDelete} disabled={!canWrite || saving}>
              {saving && <Loader2 className="animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Variant Confirmation Dialog */}
      <Dialog open={deleteVariantDialogOpen} onOpenChange={setDeleteVariantDialogOpen}>
        <DialogContent className="max-w-md rounded-2xl bg-card">
          <DialogHeader className="text-left">
            <DialogTitle className="text-[16px] font-semibold">Delete Variant</DialogTitle>
          </DialogHeader>
          <p className="text-[13.5px] text-muted-foreground">
            Are you sure you want to delete the variant "{variantToDelete?.name}"? This action cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:space-x-0">
            <Button variant="outline" size="sm" className={adminButton.outline} onClick={() => setDeleteVariantDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" className={cn(adminButton.primary, 'bg-destructive hover:bg-destructive/90')} onClick={handleDeleteVariant} disabled={!canWrite || saving}>
              {saving && <Loader2 className="animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default JobsPricingAdmin;
